import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/contexts/I18nContext';

interface SignUpStepOneProps {
  formData: {
    email: string;
    beltRank: string;
  };
  onInputChange: (field: string, value: string) => void;
  onContinue: () => void;
  loading: boolean;
}

export const SignUpStepOne = ({ formData, onInputChange, onContinue, loading }: SignUpStepOneProps) => {
  const { toast } = useToast();
  const { t } = useI18n();
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (field: string, value: string) => {
    onInputChange(field, value);
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (!formData.email) {
      setError(t('auth.step_one.email_required'));
    } else if (!validateEmail(formData.email)) {
      setError(t('auth.step_one.email_invalid'));
    } else {
      setError('');
    }
  };

  const handleContinue = async () => {
    // Check if email is empty
    if (!formData.email) {
      toast({
        title: t('auth.error'),
        description: t('auth.step_one.fill_required_fields'),
        variant: "destructive"
      });
      return;
    }

    // Check if belt rank is not selected
    if (!formData.beltRank) {
      toast({
        title: t('auth.error'),
        description: t('auth.step_one.fill_required_fields'),
        variant: "destructive"
      });
      return;
    }

    // Validate email format
    if (!validateEmail(formData.email)) {
      setError(t('auth.step_one.email_invalid'));
      return;
    }

    // Verifica se o e-mail já está cadastrado
    try {
      const { data, error } = await supabase.rpc('check_email_exists', { email_to_check: formData.email });
      if (error) {
        toast({
          title: t('auth.error'),
          description: t('auth.step_one.could_not_check_email'),
          variant: "destructive"
        });
        return;
      }
      if (data === true) {
        toast({
          title: t('auth.step_one.email_already_registered'),
          description: t('auth.step_one.email_already_registered_description'),
          variant: "destructive"
        });
        return;
      }
    } catch (err) {
      toast({
        title: t('auth.error'),
        description: t('auth.step_one.could_not_check_email'),
        variant: "destructive"
      });
      return;
    }
    // Continue to next step
    onContinue();
  };

  return (
    <form className="space-y-8 bg-[#160C0C] py-8 px-4 rounded-2xl shadow-xl" onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
      <div className="relative">
      <Input
        type="email"
        placeholder={t('auth.step_one.email_placeholder')}
        value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onBlur={handleEmailBlur}
          className={error && emailTouched ? 'border-red-500' : ''}
        />
        {error && emailTouched && (
          <p className="mt-1 text-sm text-white/80">{error}</p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-white">{t('auth.step_one.belt_rank_label')}</label>
        <Select
          value={formData.beltRank}
          onValueChange={(value) => onInputChange('beltRank', value)}
          open={open}
          onOpenChange={setOpen}
        >
          <SelectTrigger className="w-full relative [&_[data-radix-select-icon]]:hidden">
            <SelectValue placeholder={t('auth.step_one.belt_rank_placeholder')} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {open ? (
                <ChevronUp className="w-5 h-5 text-white" />
              ) : (
                <ChevronDown className="w-5 h-5 text-white" />
              )}
            </span>
        </SelectTrigger>
          <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
            <SelectItem value="white" className="text-white truncate">{t('auth.step_one.white_belt')}</SelectItem>
            <SelectItem value="blue" className="text-white truncate">{t('auth.step_one.blue_belt')}</SelectItem>
            <SelectItem value="purple" className="text-white truncate">{t('auth.step_one.purple_belt')}</SelectItem>
            <SelectItem value="brown" className="text-white truncate">{t('auth.step_one.brown_belt')}</SelectItem>
            <SelectItem value="black" className="text-white truncate">{t('auth.step_one.black_belt')}</SelectItem>
        </SelectContent>
      </Select>
      </div>

      <Button
        type="submit"
        disabled={loading || (!!error && emailTouched)}
        className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
      >
        {loading ? t('auth.step_one.loading') : t('auth.step_one.continue')}
      </Button>
    </form>
  );
};
